import { useState, useEffect, useRef, useCallback } from "react";
import imgFacial from "@/assets/treatment-facial.webp";
import imgChemicalPeel from "@/assets/treatment-chemical-peel.webp";
import imgDreamLift from "@/assets/treatment-dream-lift.webp";
import imgMicroNeedling from "@/assets/treatment-microneedling.webp";
import imgDermaplaning from "@/assets/treatment-dermaplaning.webp";
import imgLuxeLift from "@/assets/svc-luminous.webp";

const cardImages = [
  imgLuxeLift,
  imgFacial,
  imgChemicalPeel,
  imgDreamLift,
  imgMicroNeedling,
  imgDermaplaning,
];

const services = [
  { title: "LuxeLift Facial" },
  { title: "Signature Facials" },
  { title: "Chemical Peels" },
  { title: "Dream Lift" },
  { title: "Micro-Needling" },
  { title: "Dermaplaning" },
];

const ServicesCarousel = () => {
  const [rotation, setRotation] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const lastX = useRef(0);
  const lastTime = useRef(0);
  const rotationStart = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const animate = () => {
      setRotation(prev => {
        let newRotation = prev;
        if (!isDragging) {
          if (Math.abs(velocity) > 0.01) {
            newRotation = prev + velocity;
            setVelocity(v => v * 0.96);
          } else {
            const speed = isHovered ? 0.08 : 0.3;
            newRotation = prev + speed;
            setVelocity(0);
          }
        }
        return newRotation;
      });
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [isDragging, isHovered, velocity]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true); setVelocity(0);
    dragStartX.current = e.clientX; lastX.current = e.clientX;
    lastTime.current = Date.now(); rotationStart.current = rotation;
  }, [rotation]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    const now = Date.now(); const deltaTime = now - lastTime.current;
    const deltaX = e.clientX - lastX.current;
    if (deltaTime > 0) setVelocity(deltaX * 0.3 / Math.max(deltaTime / 16, 1));
    lastX.current = e.clientX; lastTime.current = now;
    const totalDeltaX = e.clientX - dragStartX.current;
    setRotation(rotationStart.current + totalDeltaX * 0.3);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => { setIsDragging(false); }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true); setVelocity(0);
    dragStartX.current = e.touches[0].clientX; lastX.current = e.touches[0].clientX;
    lastTime.current = Date.now(); rotationStart.current = rotation;
  }, [rotation]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    const now = Date.now(); const deltaTime = now - lastTime.current;
    const deltaX = e.touches[0].clientX - lastX.current;
    if (deltaTime > 0) setVelocity(deltaX * 0.3 / Math.max(deltaTime / 16, 1));
    lastX.current = e.touches[0].clientX; lastTime.current = now;
    const totalDeltaX = e.touches[0].clientX - dragStartX.current;
    setRotation(rotationStart.current + totalDeltaX * 0.3);
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => { setIsDragging(false); }, []);

  return (
    <div 
      ref={containerRef}
      className="relative h-[400px] md:h-[500px] flex items-center justify-center select-none"
      style={{ perspective: '1200px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setIsDragging(false); }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div 
        className="absolute inset-x-4 md:inset-x-16 h-full rounded-full bg-[#0a0a0a]/90 shadow-2xl overflow-hidden"
        style={{ boxShadow: '0 25px 80px -20px rgba(0,0,0,0.5), inset 0 0 60px rgba(0,0,0,0.3)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />
      </div>
      
      <div 
        className="relative flex items-center justify-center w-full cursor-grab active:cursor-grabbing"
        style={{ transformStyle: 'preserve-3d' }}
      >
        {services.map((service, index) => {
          const totalCards = services.length;
          const anglePerCard = 360 / totalCards;
          const cardAngle = index * anglePerCard + rotation;
          const radians = (cardAngle * Math.PI) / 180;
          const radius = 280;
          const translateX = Math.sin(radians) * radius;
          const translateZ = Math.cos(radians) * radius - radius;
          const rotateY = -cardAngle;
          const depth = (Math.cos(radians) + 1) / 2;
          const scale = 0.6 + depth * 0.4;
          const opacity = 0.4 + depth * 0.6;
          const zIndex = Math.round(depth * 100);
          
          return (
            <div
              key={service.title}
              className="absolute w-40 md:w-52 h-56 md:h-72 rounded-2xl overflow-hidden shadow-xl transition-opacity duration-300 group"
              style={{
                transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
                transformStyle: 'preserve-3d', zIndex, opacity,
                pointerEvents: depth > 0.5 ? 'auto' : 'none',
              }}
            >
              <img src={cardImages[index]} alt={service.title} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
              <div className="absolute inset-0 bg-black/30" />
              
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                <h3 className="text-white font-medium text-sm md:text-base">{service.title}</h3>
              </div>
              <div className="absolute inset-0 rounded-2xl border border-white/20 group-hover:border-white/40 transition-colors" />
            </div>
          );
        })}
      </div>
      
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-slate-400 text-sm">
        Drag to explore
      </div>
    </div>
  );
};

export default ServicesCarousel;
